import { RefObject, useEffect, useState } from 'react';
import { throttle } from '../helpers/throttle';

// returns boolean indicating whether position is not at top of window viewport
export function useScrolled(domRef?: RefObject<HTMLElement | null>, threshold = 0): boolean {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const scrollingElement = domRef?.current;
    const isUsingWindowScroll = scrollingElement === undefined;
    const isScrolled = isUsingWindowScroll
      ? () => {
          return window.scrollY > threshold;
        }
      : () => {
          return (scrollingElement?.scrollTop ?? 0) > threshold;
        };
    const options: AddEventListenerOptions = { passive: true };
    const [listener, clearPendingListeners] = throttle(() => {
      if (isScrolled()) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    }, 100);

    if (isUsingWindowScroll) {
      window.addEventListener('scroll', listener, options);
    } else {
      scrollingElement?.addEventListener('scroll', listener, options);
    }
    listener();
    return () => {
      if (isUsingWindowScroll) {
        window.removeEventListener('scroll', listener, options);
      } else {
        scrollingElement?.removeEventListener('scroll', listener, options);
      }
      clearPendingListeners();
    };
  }, [setIsScrolled, domRef, threshold]);

  return isScrolled;
}
